// Packages
import { Capacitor } from '@capacitor/core';
import { defineStore } from 'pinia';
import { ref } from 'vue';

// Logic
import { CONFIG } from '@hub-client/logic/logging/Config';

// Stores
import { Message, MessageBoxType, MessageType, useMessageBox } from '@hub-client/stores/messagebox';

// Other
import { fallbackLanguage } from '@hub-client/language';

// Types
enum Theme {
	System = 'system',
	Light = 'light',
	Dark = 'dark',
}

type i18nSettings = { locale: { value: string }; availableLocales: string[] };

enum TimeFormat {
	format12 = 'format12',
	format24 = 'format24',
}

enum NotificationsPermission {
	Allow = 'allow',
	Deny = 'deny',
}

enum FeatureFlag {
	signedMessages = 'signedMessages',
	dateSplitter = 'dateSplitter',
	notifications = 'notifications',
	deleteMessages = 'deleteMessages',
	editMessages = 'editMessages',
	hubSettings = 'hubSettings',
	votingWidget = 'votingWidget',
	forumRooms = 'forumRooms',
	// Implemented with issue #984
	authenticatedMedia = 'authenticatedMedia',
	unreadCounter = 'unreadCounter',
	videocalls = 'videocalls',
	consent = 'consent',
	roomLibrary = 'roomLibrary',
	phCard = 'phCard',
	whisper = 'whisper',
	chainedSession = 'chainedSession',
}

type FeatureFlags = { [key in FeatureFlag]: boolean };

interface Settings {
	/**
	 * The number of events to load on a page in a room.
	 */
	pagination: number;

	/**
	 * Max length of displayname, Matrix has as default 255, but we like a shorter one.
	 */
	displayNameMaxLength: number;

	/**
	 * UI theme: system|dark|light
	 */
	theme: Theme;

	/**
	 * timeformat: format12|format24 12 hour system or 24 hour system
	 */
	timeformat: TimeFormat;

	/**
	 * notificationsPermission: allow|deny
	 */
	notificationsPermission: NotificationsPermission;

	/**
	 * UI Language
	 * Should have type 'Language', but for some reason the build gives an error when trying to import it from '@hub-client/i18n.
	 */
	language: string;
	/**
	 * Remember the last visited hub to quickly returning to it when loading Pubhubs.
	 */
	lastHubId: string;

	_i18n?: i18nSettings;

	featureFlags: { main: FeatureFlags; stable: FeatureFlags; local: FeatureFlags };
}

let readyHandler: ((e: MessageEvent) => void) | null = null;

// Whether the system prefers a dark theme. matchMedia() itself is not reactive, so getActiveTheme
// would keep the system theme it first saw; this ref follows the media query's change events instead.
const systemPrefersDark = ref(false);
if (globalThis.matchMedia) {
	const darkQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');
	systemPrefersDark.value = darkQuery.matches;
	darkQuery.addEventListener('change', (event) => {
		systemPrefersDark.value = event.matches;
	});
}

const defaultSettings: Settings = {
	theme: Theme.System,
	timeformat: TimeFormat.format24,
	pagination: 150,
	displayNameMaxLength: 40,
	language: fallbackLanguage,
	lastHubId: '',
	_i18n: undefined,
	// First check if the Notifications API is supported.
	notificationsPermission:
		'Notification' in globalThis
			? Notification.permission === 'denied' || Notification.permission === 'default'
				? NotificationsPermission.Deny
				: NotificationsPermission.Allow
			: NotificationsPermission.Deny,

	/**
	 * Enable/disable feature flags here.
	 * Please also write down which should be enabled on main and which on stable.
	 */
	featureFlags: {
		stable: {
			signedMessages: true,
			dateSplitter: true,
			notifications: true,
			deleteMessages: true,
			editMessages: true,
			hubSettings: true,
			authenticatedMedia: true,
			unreadCounter: true,
			videocalls: false,
			roomLibrary: true,
			votingWidget: true,
			forumRooms: true,
			consent: true,
			phCard: true,
			whisper: false,
			chainedSession: false,
		},
		main: {
			signedMessages: true,
			dateSplitter: true,
			notifications: true,
			deleteMessages: true,
			editMessages: true,
			hubSettings: true,
			authenticatedMedia: true,
			unreadCounter: true,
			videocalls: true,
			roomLibrary: true,
			votingWidget: true,
			forumRooms: true,
			consent: true,
			phCard: true,
			whisper: true,
			chainedSession: false,
		},
		local: {
			signedMessages: true,
			dateSplitter: true,
			notifications: true,
			deleteMessages: true,
			editMessages: true,
			hubSettings: true,
			authenticatedMedia: true,
			unreadCounter: true,
			videocalls: true,
			roomLibrary: true,
			votingWidget: true,
			forumRooms: true,
			consent: true,
			phCard: true,
			whisper: true,
			chainedSession: false,
		},
	},
};

const useSettings = defineStore('settings', {
	state: () => ({
		...defaultSettings,
		isMobileState: true as boolean | undefined,
		// Whether we run inside the native app. Correct as-is in the global client,
		// which is the frame Capacitor exposes its bridge to; the hub client always
		// reads false here and is told the real value over the messagebox.
		isNativeApp: Capacitor.isNativePlatform(),
		// Whether that native app is a debug build. Same story as isNativeApp.
		isNativeDebugApp: Capacitor.DEBUG ?? false,
	}),

	getters: {
		getPagination: (state: Settings) => state.pagination,
		getDisplayNameMaxLength: (state: Settings) => state.displayNameMaxLength,

		/**
		 * Get theme set in preferences
		 */
		getSetTheme: (state: Settings): Theme => {
			return state.theme;
		},

		/**
		 * Get theme set in preferences, and if 'system' give the 'light' or 'dark' theme depending on system.
		 */
		getActiveTheme: (state: Settings): Theme.Light | Theme.Dark => {
			if (state.theme !== Theme.System) {
				return state.theme;
			}
			if (systemPrefersDark.value) {
				return Theme.Dark;
			}
			return Theme.Light;
		},

		getActiveLanguage: (state: Settings): string => {
			return state.language;
		},

		getTimeFormat: (state: Settings): TimeFormat => {
			return state.timeformat;
		},
		getLastVisitedHub: (state: Settings): string => {
			return state.lastHubId;
		},

		getNotificationsPermission: (state: Settings): NotificationsPermission => {
			return state.notificationsPermission;
		},

		/**
		 * Get timeformats as options (for form selecting).
		 * The function must be the localisation function $t.
		 */
		getTimeFormatOptions: () => (formats: (key: string) => string) => {
			const options = Object.values(TimeFormat).map((e) => {
				return { label: formats('timeformats.' + e), value: e };
			});
			return options;
		},

		/**
		 * Get themes as options (for form selecting).
		 * If nothing is given the label will be a capitalized version of the theme. If a function is given, the function will be used to generate the label. So you can give the localisation function $t.
		 */
		getThemeOptions: () => (themes: ((key: string) => string) | undefined) => {
			const options = Object.values(Theme).map((e) => {
				if (typeof themes === 'function') {
					return { label: themes('themes.' + e), value: e };
				} else {
					return { label: e.charAt(0).toUpperCase() + e.slice(1), value: e };
				}
			});
			return options;
		},

		getLanguageOptions: (state: Settings) => {
			const options = state._i18n?.availableLocales?.map((e: string) => {
				return { label: e.toUpperCase(), value: e };
			});
			return options;
		},

		getNotificationOptions: () => (notifications: ((key: string) => string) | undefined) => {
			const options = Object.values(NotificationsPermission).map((e) => {
				if (typeof notifications === 'function') {
					return { label: notifications('notifications.' + e), value: e };
				} else {
					return { label: e.charAt(0).toUpperCase() + e.slice(1), value: e };
				}
			});
			return options;
		},
	},

	actions: {
		initI18b(init: i18nSettings) {
			this._i18n = init;
			this.language = init.locale.value;
		},

		setPagination(newPagination: number) {
			this.pagination = newPagination;
		},

		setTheme(newTheme: Theme, send: boolean = false) {
			if (this.theme !== newTheme) {
				this.theme = newTheme;
				if (send) this.sendSettings();
			}
		},

		setTimeFormat(format: TimeFormat) {
			this.timeformat = format;
		},

		setNotificationPermission(perm: NotificationsPermission) {
			this.notificationsPermission = perm;
		},

		setLanguage(newLanguage: string, send: boolean = false) {
			if (this.language !== newLanguage && (this._i18n?.availableLocales.indexOf(newLanguage) ?? -1) >= 0) {
				this.language = newLanguage;
				if (send) this.sendSettings();
			}
		},
		setLastVisitedHub(hubId: string) {
			this.lastHubId = hubId;
		},

		setIsNativeApp(isNativeApp: boolean) {
			this.isNativeApp = isNativeApp;
		},

		setIsNativeDebugApp(isNativeDebugApp: boolean) {
			this.isNativeDebugApp = isNativeDebugApp;
		},

		sendSettings() {
			const messagebox = useMessageBox();
			if (messagebox.type === MessageBoxType.Parent) {
				messagebox.sendMessage(
					new Message(MessageType.Settings, {
						theme: this.theme as Theme,
						timeformat: this.timeformat as TimeFormat,
						language: this.language,
						isNativeApp: this.isNativeApp,
						isNativeDebugApp: this.isNativeDebugApp,
					}),
				);
			}
		},

		/**
		 * Checks whether a feature is enabled or not, for more control in the transition from development to production.
		 * Add features in the settins store featureFlags property.
		 */
		isFeatureEnabled(feature: FeatureFlag): boolean {
			switch (CONFIG.productionMode) {
				case 'development':
					return this.featureFlags.main[feature];
				case 'local development':
					return this.featureFlags.local[feature];
				case 'production':
				default:
					return this.featureFlags.stable[feature];
			}
		},

		updateIsMobile() {
			const isMobile = window.innerWidth < 1024;
			this.isMobileState = isMobile;

			const iframe = document.getElementById('hub-frame-id') as HTMLIFrameElement | null;
			if (iframe?.contentWindow) {
				iframe.contentWindow.postMessage({ type: 'viewport-update', isMobileState: isMobile }, '*');
			}
		},

		startListeningMobile() {
			this.updateIsMobile();
			window.addEventListener('resize', this.updateIsMobile);

			readyHandler = (event: MessageEvent) => {
				if (event.data?.type === 'viewport-ready') {
					this.updateIsMobile();
				}
			};
			window.addEventListener('message', readyHandler);
		},

		stopListeningMobile() {
			window.removeEventListener('resize', this.updateIsMobile);

			if (readyHandler) {
				window.removeEventListener('message', readyHandler);
			}
		},
	},
});

type SettingsStore = ReturnType<typeof useSettings>;

export { defaultSettings, FeatureFlag, Settings, Theme, TimeFormat, NotificationsPermission, useSettings, type i18nSettings, SettingsStore };
